// Direct module imports, NOT the ".." root barrel: the barrel re-exports this file, and that
// cycle left this module's exports (ApiResult & co.) in a temporal dead zone whenever an app's
// first import entered through api.gen — react-refresh enumerates barrel exports eagerly in dev,
// so the console app crashed on boot with "Cannot access 'ApiResult' before initialization".
import { ApiResponse } from "../models/ApiResponse";
import { OmniUser } from "../models/OmniUser.gen";
import { sessionValue } from "../helpers/sessionValue";
import { sleep } from "../helpers/utilHelpers";


const AUTH_OPTIONS: RequestInit = {
  credentials: 'include'
};
Object.freeze(AUTH_OPTIONS);

const DEFAULT_GET_OPTIONS: RequestInit = {
  ...AUTH_OPTIONS,
  method: 'GET',
  headers: {
    Accept: 'application/json',
  },
};
Object.freeze(DEFAULT_GET_OPTIONS);

const DEFAULT_POST_OPTIONS: RequestInit = {
  ...DEFAULT_GET_OPTIONS,
  headers: {
    ...DEFAULT_GET_OPTIONS.headers,
    'Content-Type': 'application/json',
  },
  method: 'POST',
};
Object.freeze(DEFAULT_POST_OPTIONS);

const DEFAULT_PUT_OPTIONS: RequestInit = {
  ...DEFAULT_GET_OPTIONS,
  headers: {
    ...DEFAULT_GET_OPTIONS.headers,
    'Content-Type': 'application/json',
  },
  method: 'PUT',
};
Object.freeze(DEFAULT_PUT_OPTIONS);

const DEFAULT_PATCH_OPTIONS: RequestInit = {
  ...DEFAULT_GET_OPTIONS,
  headers: {
    ...DEFAULT_GET_OPTIONS.headers,
    'Content-Type': 'application/json',
  },
  method: 'PATCH',
};
Object.freeze(DEFAULT_PATCH_OPTIONS);

const DEFAULT_DELETE_OPTIONS: RequestInit = {
  ...DEFAULT_GET_OPTIONS,
  method: 'DELETE'
};
Object.freeze(DEFAULT_DELETE_OPTIONS);

const RAW_POST_OPTIONS: RequestInit = {
  ...AUTH_OPTIONS,
  method: 'POST'
};
Object.freeze(RAW_POST_OPTIONS);

// Not spread from AUTH_OPTIONS on purpose: the whole point is that no cookie rides along.
const ANONYMOUS_GET_OPTIONS: RequestInit = {
  credentials: 'omit',
  method: 'GET',
  headers: {
    Accept: 'application/json',
  },
};
Object.freeze(ANONYMOUS_GET_OPTIONS);

const REQUEST_TIMEOUT_MS = 30000;

const REACT_APP_CORE_API_ROOT_URL = process.env.REACT_APP_CORE_API_ROOT_URL;

export type ApiResult<T> = Promise<T | undefined>;
export const ApiResult = Promise;

class OmniError extends Error {
  private _messages: string[];
  public get messages() { return this._messages; }
  /** The HTTP status, where one was received. Lets a caller tell a 409 from a 404. */
  public readonly status?: number;

  constructor(messages: string[], status?: number) {
    const message = messages?.join("  ");
    super(message);

    this._messages = messages;
    this.status = status;
    this.name = 'OmniError';
  }
}

export class UserError extends OmniError {
  constructor(messages: string[], status?: number) {
    super(messages, status);
    this.name = 'UserError';
  }
}

export class ServerError extends OmniError {
  constructor(messages: string[]) {
    super(messages);
    this.name = 'ServerError';
  }
}

export class UnauthorizedError extends OmniError {
  constructor(messages: string[]) {
    super(messages);
    this.name = 'UnauthorizedError';
  }
}

export class TimeoutError extends OmniError {
  constructor() {
    super(['The server did not respond in a timely manner.']);
    this.name = 'TimeoutError';
  }
}
export class ServerUnavailableError extends OmniError {
  constructor() {
    super(['The server is not currently available.']);
    this.name = 'ServerUnavailableError';
  }
}
function isUnauthError(statusCode: number): boolean {
  return statusCode === 401;
}

function isUserError(statusCode: number): boolean {
  return statusCode >= 400 && statusCode < 500;
}

function isServerError(statusCode: number): boolean {
  return statusCode >= 500 && statusCode < 600;
}

function validateResponse(status?: number, errors?: string[]): void {
  if (status === 503) { throw new ServerUnavailableError(); }
  if (!!!status || !!!errors) { throw new TimeoutError(); }
  if (isUnauthError(status)) { throw new UnauthorizedError(errors); }
  if (isUserError(status)) { throw new UserError(errors, status); }
  if (isServerError(status)) { throw new ServerError(errors); }
}

export async function getValidatedResponseData<T>(response: Response): ApiResult<T> {
  const responseText = await response.text();

  if (responseText.length === 0) {
    validateResponse(response.status, [response.statusText]);
    return;
  }

  const { status, error, data } = JSON.parse(responseText) as ApiResponse<T>;
  validateResponse(status, [error]);
  // ApiResult<T> only ever promised T | undefined. Passing null through had the generated clients
  // default-construct a model, so its field initialisers stood in for data that was never sent.
  return data ?? undefined;
}

function addAuthForOptions(options: RequestInit): RequestInit {
  const { headers } = options;
  const [user] = sessionValue<OmniUser>('currentUser');

  if (options.method === 'GET') { return options; }

  const authenticatedHeaders = { ...headers, 'X-AUTH-TOKEN': user?.authToken || '' };
  return { ...options, headers: authenticatedHeaders };
}

async function rawfetch(path: string, options: RequestInit): Promise<Response> {
  const authenticatedOptions = addAuthForOptions(options);
  return fetch(getApiUrl(path), authenticatedOptions);
}

async function apiFetch<T>(path: string, options: RequestInit): ApiResult<T> {
  const getResponse = async () => {
    const authenticatedOptions = addAuthForOptions(options);
    const response = await fetchWithTimeout(getApiUrl(path), { ...authenticatedOptions, timeout: REQUEST_TIMEOUT_MS });
    return response;
  };

  return getValidatedResponseData<T>(await getResponse()).catch(async error => {
    if (error instanceof UnauthorizedError) {
      console.warn("Trying to re-authenticate the client...");
      document.dispatchEvent(new Event('auth:checkAuthState'));
      //Give it a few seconds to hopefully get our session refreshed
      //TODO: Maybe we remove this and add code to the AuthContextProvider that will keep our user updated and warn a user if internet connection is lost... 
      await sleep(3000);
      return getValidatedResponseData<T>(await getResponse());
    } else if (error instanceof ServerUnavailableError) {
      document.dispatchEvent(new Event('auth:maintenanceMode'));
    } else {
      throw error;
    }
  });
}
type RequestInitTimeout = RequestInit & { timeout: number };
async function fetchWithTimeout(url: string, options: RequestInitTimeout): Promise<Response> {
  const { timeout, ...fetchOptions } = options;

  // Create an AbortController instance
  const controller = new AbortController();

  // Set a timeout using setTimeout
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    // Perform the fetch with the signal option set to the AbortController's signal
    const response = await fetch(url, { ...fetchOptions, signal: controller.signal });

    // If the request was successful, clear the timeout
    clearTimeout(timeoutId);

    return response;
  } catch (error) {
    // Handle errors (e.g., network error, aborted request)
    clearTimeout(timeoutId);
    throw error;
  }
}
/*.catch(error => {
    console.log("What is this error: ", error);
    if (error instanceof UnauthorizedError) {
      console.warn("Trying to re-authenticate the client...");
      return fetch(input, init);
    } else {
      throw error;
    }
  });
  */
export function getApiUrl(path: string) {
  // firmware_webconfig can be served through another device's reverse proxy at
  // /proxy/{device}/; the firmware then injects window.__PROXY_BASE__, and every
  // device-scoped URL must carry it or the request escapes the proxy prefix and
  // silently hits the gateway device instead of the proxied one (a cross-device
  // write path — e.g. Devices.save / DeviceCommands.sendCommand). It is
  // undefined in the dashboard, which is never proxied, so this branch is a
  // no-op there. Mirrors firmware_webconfig's apiRoot() in src/lib/utils.ts.
  const proxyBase =
    typeof window !== 'undefined'
      ? (window as { __PROXY_BASE__?: string }).__PROXY_BASE__
      : undefined;
  return [proxyBase ?? REACT_APP_CORE_API_ROOT_URL, path].join('');
}

export async function get<T>(path: string): ApiResult<T> {
  return apiFetch<T>(path, DEFAULT_GET_OPTIONS);
}

/**
 * GET against an endpoint the server publishes to anyone holding the link — the clip pages behind
 * `Startup.PublicClipCorsPolicy` (`/api/events/clip-info`), which is `AllowAnyOrigin()`.
 *
 * Sends nothing that identifies the caller: no auth-token header, and `credentials: 'omit'` so the
 * browser withholds cookies as well. That last part is load-bearing. Those endpoints answer every
 * origin with a wildcard Access-Control-Allow-Origin, and a browser refuses a wildcard on any request
 * that carried credentials — which `get()` and every generated client always do. Calling one through
 * the generated client from app.omnisightusa.com (or cab./console.) fails with a CORS error before
 * the page ever sees the 200 the server returned.
 *
 * Deliberately not routed through `apiFetch`: there is no session behind this call, so a 401 is an
 * answer to surface rather than a dropped session to re-authenticate and retry.
 */
export async function getAnonymous<T>(path: string): ApiResult<T> {
  const response = await fetchWithTimeout(getApiUrl(path), { ...ANONYMOUS_GET_OPTIONS, timeout: REQUEST_TIMEOUT_MS });
  return getValidatedResponseData<T>(response);
}

export async function del<T>(path: string): ApiResult<T> {
  return apiFetch<T>(path, DEFAULT_DELETE_OPTIONS);
}

export async function rawPost(path: string, body?: any, requestOptions?: RequestInit): Promise<Response> {
  const options = { ...RAW_POST_OPTIONS, body: body, ...requestOptions };
  return rawfetch(path, options);
}

export async function post<T>(path: string, body?: any): ApiResult<T> {
  const options = { ...DEFAULT_POST_OPTIONS, body: body ? JSON.stringify(body) : undefined };
  return apiFetch<T>(path, options);
}

export async function put<T>(path: string, body?: any): ApiResult<T> {
  const options = { ...DEFAULT_PUT_OPTIONS, body: body ? JSON.stringify(body) : undefined };
  return apiFetch<T>(path, options);
}

export async function patch<T>(path: string, body?: any): ApiResult<T> {
  const options = { ...DEFAULT_PATCH_OPTIONS, body: body ? JSON.stringify(body) : undefined };
  return apiFetch<T>(path, options);
}